import { type TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { HeadlineL, LabelS } from '@sorare/core/src/atoms/typography';
import { ConfirmDialog } from '@sorare/core/src/components/form/ConfirmDialog';
import { cart } from '@sorare/core/src/lib/glossary';

import { CardsShow } from 'components/card/CardsShow';

import type { AddWatchlistToCartConfirmDialog_anyCard } from './__generated__/index.graphql';

const Root = styled(Vertical).attrs({ center: true })`
  padding: var(--double-unit);
  height: 100%;
  justify-content: space-between;
`;

type Props = {
  open: boolean;
  onConfirm: () => Promise<void>;
  onClose: () => void;
  cards: AddWatchlistToCartConfirmDialog_anyCard[];
};

export const AddWatchlistToCartConfirmDialog = ({
  open,
  onConfirm,
  onClose,
  cards,
}: Props) => {
  const max5Cards = cards.slice(0, 5);
  return (
    <ConfirmDialog
      open={open}
      onConfirm={onConfirm}
      hideCancel
      message={
        <Root className="text-center" gap={3}>
          <Vertical gap={3}>
            <CardsShow cards={max5Cards} specialFive />
            <HeadlineL className="text-pretty">
              <FormattedMessage
                id="AddWatchlistToCartButton.modal.headline"
                defaultMessage="You're about to add {count, plural, one {# card} other {# cards}} to your cart!"
                values={{
                  count: cards.length,
                }}
              />
            </HeadlineL>
          </Vertical>
        </Root>
      }
      onClose={onClose}
      cta={<FormattedMessage {...cart.addToCart} />}
      disclaimer={
        <LabelS color="var(--c-nd-600)" className="text-center">
          <FormattedMessage
            id="AddWatchlistToCartButton.modal.disclaimer"
            defaultMessage="Cards not available on Instant Buy right now are not added to cart"
          />
        </LabelS>
      }
    />
  );
};

AddWatchlistToCartConfirmDialog.fragments = {
  anyCard: gql`
    fragment AddWatchlistToCartConfirmDialog_anyCard on AnyCardInterface {
      slug
      ...CardsShow_anyCard
    }
    ${CardsShow.fragments.anyCard}
  ` as TypedDocumentNode<AddWatchlistToCartConfirmDialog_anyCard>,
};
